// Headless side-effect component that bridges the React Query cache
// (server-truth physics tuning blob) into the in-memory singleton
// that the renderer reads every frame.
//
// Why a component, not a hook called inline somewhere: the renderer
// (apps/web/src/lib/virtual3d.ts) calls getTuning() synchronously
// per frame and lives outside React. So we keep the imperative
// singleton API in virtual3d-tuning.ts and mount this component
// once at the top of the tree to keep it in sync with the server.
//
// Also handles one-shot localStorage cleanup of the legacy
// `cornholio.physicsTuning` key. That used to shadow server values
// (because old code read it on import); the read path is gone now,
// but old browsers still have the key sitting on disk. Drop it on
// first mount so the slot isn't taken if we ever reclaim the name.

import { useEffect } from "react";
import { usePhysicsTuningQuery } from "@/lib/queries";
import {
  LEGACY_STORAGE_KEY,
  setTuning,
  type PhysicsTuning,
} from "@/lib/virtual3d-tuning";

export function PhysicsTuningSync() {
  // Boot-time legacy localStorage cleanup. Idempotent — fine to run
  // on every mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* private mode / quota — ignore */
    }
  }, []);

  const { data } = usePhysicsTuningQuery();
  useEffect(() => {
    if (!data) return;
    // Server blob is a free-form Record<string, unknown>; we trust the
    // FE knob names and let deepMergeTuning (inside setTuning) merge
    // it into the singleton. Unknown keys are harmless — they just
    // shadow nothing the renderer reads.
    setTuning(data as Partial<PhysicsTuning>);
  }, [data]);

  return null;
}
